import type { FilterAnchor } from '@ofmos/shared/filter-ast';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAppClient } from '../../data/ClientProvider';
import { queryKeys } from '../../data/queryKeys';
import type { RelationContext, RelationNeed } from './filterModel';

/**
 * D27's ANCHOR SET AND THE FIELDS BEHIND IT, for one filter, at the depth it actually uses.
 *
 * THREE LEVELS, and never more, because D7 caps a filter at two hops:
 *
 *  - level 0 is the object being filtered. Its anchor set is what the picker offers and
 *    what `pruneFilter` validates against, so it is read unconditionally;
 *  - level 1 is every object the filter's own related nodes reach. Their FIELDS are what an
 *    inner condition compares and their ANCHORS are what a second hop may pick from;
 *  - level 2 is every object a SECOND hop reaches. Only their fields are needed - a third
 *    hop is refused at four entrances (D7), so nothing reads their anchors.
 *
 * IT LOADS WHAT THE FILTER NAMES, NOT WHAT THE MODEL COULD REACH. Reading the fields of
 * every anchorable object on every list page would be a dozen metadata requests behind a
 * screen that may carry no related filter at all. `needs` carries anchor FIELD ids, which
 * a stored node and a live draft can both produce with no metadata - so the resolution
 * runs level by level as each read lands, and settles in as many renders as the filter has
 * hops.
 *
 * `staleTime: Infinity` throughout, following `useInboundRelations`: this is the metadata
 * graph, it changes when an admin changes the model, and both consumers mount and unmount
 * many times per session (INV-13).
 */
export function useRelationContext(
  objectId: string,
  needs: readonly RelationNeed[],
): RelationContext {
  const client = useAppClient();

  const anchorQuery = (id: string) => ({
    queryKey: queryKeys.filterAnchors(id),
    queryFn: () => client.metadata.filterAnchors(id),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: id !== '',
  });
  const fieldQuery = (id: string) => ({
    queryKey: queryKeys.fields(id),
    queryFn: () => client.metadata.listFields(id),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: id !== '',
  });

  const level0 = useQueries({
    queries: [anchorQuery(objectId)],
    combine: (results) => results[0]?.data,
  });

  const level1Ids = useMemo(
    () =>
      resolve(
        level0,
        needs.map((need) => need.anchorFieldId),
      ),
    [level0, needs],
  );

  const level1Anchors = useQueries({
    queries: level1Ids.map(anchorQuery),
    combine: (results) => byObject(level1Ids, results),
  });
  const level1Fields = useQueries({
    queries: level1Ids.map(fieldQuery),
    combine: (results) => byObject(level1Ids, results),
  });

  /**
   * A SECOND HOP'S OBJECT IS RESOLVED AGAINST THE FIRST HOP'S ANCHOR SET, which is why the
   * nested anchor ids travel grouped by their parent rather than in one flat list: the same
   * field id could be an anchor on two different objects, and asking the wrong set for it
   * would silently load the wrong fields.
   */
  const level2Ids = useMemo(() => {
    const found = new Set<string>();
    for (const need of needs) {
      if (need.nested.length === 0) continue;
      const parentId = resolve(level0, [need.anchorFieldId])[0];
      if (parentId === undefined) continue;
      for (const id of resolve(level1Anchors.get(parentId), need.nested)) found.add(id);
    }
    return [...found];
  }, [level0, level1Anchors, needs]);

  const level2Fields = useQueries({
    queries: level2Ids.map(fieldQuery),
    combine: (results) => byObject(level2Ids, results),
  });

  return useMemo(
    () => ({
      objectId,
      anchorsOf: (id: string) => (id === objectId ? level0 : level1Anchors.get(id)),
      fieldsOf: (id: string) => level1Fields.get(id) ?? level2Fields.get(id),
    }),
    [objectId, level0, level1Anchors, level1Fields, level2Fields],
  );
}

/** The related objects a set of anchor field ids names, deduplicated, in a stable order. */
function resolve(
  anchors: readonly FilterAnchor[] | undefined,
  anchorFieldIds: readonly string[],
): readonly string[] {
  if (anchors === undefined) return [];
  const found = new Set<string>();
  for (const anchorFieldId of anchorFieldIds) {
    const anchor = anchors.find((entry) => entry.anchorFieldId === anchorFieldId);
    if (anchor !== undefined) found.add(anchor.relatedObjectId);
  }
  return [...found].sort();
}

function byObject<T>(
  ids: readonly string[],
  results: readonly { readonly data: T | undefined }[],
): ReadonlyMap<string, T> {
  const map = new Map<string, T>();
  for (const [index, id] of ids.entries()) {
    const data = results[index]?.data;
    if (data !== undefined) map.set(id, data);
  }
  return map;
}
